import { memo, type ReactNode } from 'react';

import type { AddressFields } from '../../format/address';
import { useFormat } from '../useFormat';
import { DEFAULT_EMPTY_VALUE, type EmptyProps } from './shared';

export type AddressProps = EmptyProps & {
  /** The address. Every field is optional; absent ones collapse rather than leaving a gap. */
  readonly fields: AddressFields;
  /**
   * Render the lines an envelope would carry, separated by breaks, instead of one line.
   *
   * One line suits a table cell; lines suit a card or a detail panel.
   */
  readonly lines?: boolean;
};

/**
 * Whether there is anything at all to show. An address of only blank fields is empty.
 *
 * Written against `unknown` rather than the declared `string | undefined`, because the fields
 * arrive from an API response and a `null` in one of them is a JSON payload's business, not a
 * reason to take the page down. The formatter guards the same way.
 */
const isBlankAddress = (fields: AddressFields): boolean =>
  Object.values(fields as Record<string, unknown>).every(
    (field) => typeof field !== 'string' || field.trim() === ''
  );

/**
 * A postal address in the reader's national layout.
 *
 * The layout decides field order and punctuation, so a Mexican address is not a US one with the
 * words swapped.
 */
const Address = memo(
  ({
    fields,
    lines = false,
    emptyValue = DEFAULT_EMPTY_VALUE,
  }: AddressProps): JSX.Element => {
    const format = useFormat();

    if (isBlankAddress(fields)) return <>{emptyValue}</>;

    if (!lines) return <>{format.address(fields)}</>;

    // Nested fragments rather than an array of lines: JSX children are positional and so need no
    // keys, and two lines of an address can legitimately read the same, leaving nothing to key on.
    return (
      <>
        {format.addressLines(fields).reduce<ReactNode>((rendered, line) => {
          if (rendered === null) {
            return line;
          }
          return (
            <>
              {rendered}
              <br />
              {line}
            </>
          );
        }, null)}
      </>
    );
  }
);

Address.displayName = 'Address';

export default Address;
